/**
 * Permit-to-Work templates.
 *
 * Permits are short-lived authorisations issued for a specific task, on a
 * specific site, for a specific person/team. They have a distinct lifecycle:
 *
 *   open → active → closed (or expired/cancelled)
 *
 * Each template carries:
 *   - id, title, type, default duration and risk
 *   - prerequisites (the things that must be true before issue)
 *   - controls (the things that must be in place during work)
 *   - signOffChecks (the boxes the issuer must tick before issuing)
 *   - completionChecks (the boxes the closer must tick before closing)
 */

export type PermitType = 'hot_works' | 'dig' | 'confined_space' | 'height' | 'lock_out_tag_out' | 'custom';

export interface PermitTemplate {
  id: string;
  title: string;
  type: PermitType;
  description: string;
  defaultDurationHours: number;             // typical max validity from issue
  prerequisites: string[];                  // before issuing
  controls: string[];                       // during work
  signOffChecks: string[];                  // ticked by issuer before issuing
  completionChecks: string[];               // ticked by closer before closing
  fireWatchMinutes?: number;                // hot works only
}

export const PERMIT_TEMPLATES: PermitTemplate[] = [
  // ============================================================
  {
    id: 'hot_works',
    title: 'Hot Works Permit',
    type: 'hot_works',
    description: 'Welding, brazing, cutting, grinding or any work involving open flame or sparks. CDM-compliant permit-to-work with mandatory fire-watch period.',
    defaultDurationHours: 8,
    fireWatchMinutes: 60,
    prerequisites: [
      'Operative is competent for the specific hot work being undertaken',
      'Combustibles cleared 10 metres from the work area',
      'Fire-resistant blanket / heat mat in place behind work',
      'CO₂ extinguisher (5 kg minimum) within reach',
      'Fire watch person nominated, briefed, and physically present',
      'Atmospheric check carried out if in any confined / enclosed space',
      'No hot works to commence in last 90 minutes of working day',
    ],
    controls: [
      'Fire watch person remains on watch throughout work AND for 60 minutes after last spark / flame',
      'Hot work tools never left unattended whilst hot',
      'Gas cylinders upright, secured, valves closed when not in use',
      'Flashback arrestors fitted to oxy-acetylene hoses',
      'Personal RPE (FFP3 minimum or A1P3 for stainless / galvanised) worn',
      'Welding leathers / FR clothing worn',
      'Work area cordoned and signed',
    ],
    signOffChecks: [
      'I have walked the work area and confirmed combustibles are clear',
      'Fire-watch person nominated and briefed',
      'CO₂ extinguisher in place and serviceable',
      'Operative is competent and PPE is correct',
      'I am authorised to issue Hot Works Permits at this site',
    ],
    completionChecks: [
      'All hot works ceased',
      'Workpiece cooled — no hot metal remaining',
      'Fire watch maintained for full 60 minutes after last spark',
      'Work area inspected and confirmed safe',
      'Gas cylinders secured / removed from area',
      'Permit cancelled in writing on this record',
    ],
  },

  // ============================================================
  {
    id: 'dig',
    title: 'Permit-to-Dig',
    type: 'dig',
    description: 'Mechanical or hand excavation. Mandatory CAT-scan + service drawings before any breakout. Prevents service strikes — gas, electric, water, comms.',
    defaultDurationHours: 24,
    prerequisites: [
      'Service drawings reviewed for the dig location',
      'CAT-scan + Genny carried out over the dig footprint within last 24 hours',
      'Service strike points marked on the ground',
      'Operative briefed on hand-dig zone (last 300 mm to known services)',
      'Banksman / spotter on hand for mechanical dig',
      'Excavation will be barriered and lit at all times when open',
    ],
    controls: [
      'Hand-dig within 300 mm of any known service — never mechanical',
      'CAT-scan progressively as the dig deepens',
      'Insulated tools for hand-digging in service zones',
      'Excavation sides battered or shored above 1.2 m depth',
      'Daily inspection by competent person before any entry',
      'Pedestrian / vehicle barriers around all open excavations',
    ],
    signOffChecks: [
      'Service drawings reviewed and on site',
      'CAT-scan carried out within last 24 hours',
      'Banksman briefed (if mechanical dig)',
      'Operative is competent for hand-dig in service zones',
      'I am authorised to issue Permits-to-Dig at this site',
    ],
    completionChecks: [
      'Excavation completed without service strike',
      'OR — service strike incident reported separately',
      'Excavation backfilled OR barriered for next shift',
      'Permit cancelled in writing on this record',
    ],
  },

  // ============================================================
  {
    id: 'confined_space',
    title: 'Permit-to-Enter Confined Space',
    type: 'confined_space',
    description: 'Entry to manholes, chambers, tanks, voids. Atmospheric monitoring mandatory. Rescue plan briefed before entry. NEVER alone.',
    defaultDurationHours: 8,
    prerequisites: [
      '4-gas atmospheric monitor calibrated within last 30 days',
      'Rescue plan briefed and rescuer at the surface',
      'Tripod / davit + harness + lifeline rigged for any vertical entry',
      'Inflows (water, sewage) isolated and locked off',
      'Two-way radios working between entrant and surface',
      'Lighting (intrinsically safe if any flammable risk) in place',
      'NEVER alone — minimum 2 in space + 1 surface rescuer',
    ],
    controls: [
      'Continuous atmospheric monitoring during occupation',
      'Atmosphere alarms — exit immediately',
      'O₂ in 19.5–23.5%, LEL <10%, CO <50 ppm, H₂S <10 ppm',
      'Communication every 10 min minimum with surface',
      'Escape RPE on each entrant',
      'Entrant exits at end of permit duration regardless of progress',
    ],
    signOffChecks: [
      '4-gas monitor calibration date confirmed',
      'Rescue plan briefed; rescuer present and ready',
      'Inflows isolated and locked off; verified by zero-flow check',
      'Entrants are competent and have valid confined-space training',
      'I am authorised to issue Permits-to-Enter Confined Space at this site',
    ],
    completionChecks: [
      'All entrants out of the space',
      'Atmospheric readings logged',
      'Tripod / harness / lifeline removed and stowed',
      'Inflows can be restored',
      'Permit cancelled in writing on this record',
    ],
  },

  // ============================================================
  {
    id: 'height',
    title: 'Working at Height Permit',
    type: 'height',
    description: 'Permit for work at height where collective edge protection is not feasible — e.g. roof work, fascia / soffit on a tower, leadwork at abutment.',
    defaultDurationHours: 8,
    prerequisites: [
      'Edge protection considered first; permit only issued if not reasonably practicable',
      'Harness inspected within last 6 months; lanyard within last 6 months',
      'Anchor point identified and rated for the load',
      'Suspension trauma / rescue plan briefed; rescuer ready',
      'Wind / weather check — STOP threshold agreed',
      'Operative trained for working at height (e.g. PASMA, IPAF, IRATA depending on access)',
    ],
    controls: [
      'Twin lanyard fall restraint always anchored',
      'Tools on lanyards or in bag — never carried loose at height',
      'No-go zone below the work area; helmets mandatory below',
      'STOP at sustained wind >25 mph or in lightning',
      'Rescue plan can be activated within 15 min (suspension trauma window)',
      'Pair work — never alone at height under this permit',
    ],
    signOffChecks: [
      'Edge protection considered and ruled out for this task',
      'Harness, lanyard and anchor inspected and rated',
      'Rescue plan briefed; rescuer present',
      'Operative is competent and wearing correct PPE',
      'I am authorised to issue Working at Height Permits at this site',
    ],
    completionChecks: [
      'Operative back at ground level',
      'No fall arrest activations during the permit',
      'OR — fall arrest activation reported separately',
      'Equipment inspected post-use; defects flagged',
      'Permit cancelled in writing on this record',
    ],
  },

  // ============================================================
  {
    id: 'lock_out_tag_out',
    title: 'Lock-out / Tag-out Permit',
    type: 'lock_out_tag_out',
    description: 'Isolation of mechanical, electrical, hydraulic, pneumatic or gas energy sources before maintenance / modification work. Each isolation point locked and tagged.',
    defaultDurationHours: 8,
    prerequisites: [
      'Energy source(s) identified — electrical, mechanical, hydraulic, pneumatic, gas, thermal',
      'Isolation points identified for each source',
      'Locks and tags ready for each isolation point',
      'Stored energy considered (capacitors, springs, raised loads, pressurised lines)',
      'Test equipment ready to verify zero energy state',
      'Operative trained in safe isolation for the energy source(s)',
    ],
    controls: [
      'Each isolation point locked AND tagged with name, date and reason',
      'Verify zero energy state — test before, between, after (proven dead-live-dead for electrical)',
      'Stored energy released or restrained',
      'Lock keys held by the operative carrying out the work — never by another',
      'Multi-trade work — each trade applies their own lock to a multi-lock hasp',
    ],
    signOffChecks: [
      'All energy sources identified',
      'All isolation points locked AND tagged',
      'Zero energy state verified at each point',
      'Stored energy released or restrained',
      'Operative is competent for safe isolation of the energy source(s)',
      'I am authorised to issue Lock-out / Tag-out Permits at this site',
    ],
    completionChecks: [
      'Work completed; equipment ready to re-energise',
      'All tools, debris and personnel clear',
      'Locks removed by the person who applied them',
      'Tags removed',
      'Energy restored progressively; equipment tested for safe operation',
      'Permit cancelled in writing on this record',
    ],
  },
];
